import { Alert, Box, Button, Stack, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material"
import { useEffect, useMemo } from "react"
import { Else } from "lib/@components/else"
import { If } from "lib/@components/if"
import { NoResults } from "lib/@components/no-results/no-results"
import { Repeat } from "lib/@components/repeat"
import { SearchBoxUsingSearchParam } from "lib/@components/searchBoxUsingSearchParam"
import { useReferenceState } from "lib/@hooks/use-reference"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { useRefresh } from "lib/@hooks/useRefresh"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { PageBreadcrumbContainer } from "lib/page-breadcrumb-container"
import { useDialog } from "lib/@hooks/useDialog"
import { useScrollRestoration } from "lib/use-scroll-restoration"
import { TreeToolbar } from "slot-definitions"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { SortableTableCell } from "routes/schedule/plugins/legislation-resolutions/sortable-table-cell"
import { useLocation } from "react-router-dom"
import { getProjectBridgeOverview } from "./controller/get-project-bridge-overview"
import { uploadProjectBridgeReport } from "./controller/upload-project-bridge-report"
import { ProjectBridgeImportSummaryCards } from "./project-bridge-import-summary-cards"
import { startProjectBridgeGeneration } from "./controller/start-project-bridge-generation"
import { ProjectBridgeListRow } from "./project-bridge-list-row"
import { getProjectBridgeListScrollKey } from "./project-bridge-list-presenters"
import { ProjectBridgeUploadDialog } from "./project-bridge-upload-dialog"
import { clearProjectBridgeScheduleSelection } from "./project-bridge-selection"
import { ProjectBridgeCreateWorkPackageButton } from "./project-bridge-create-work-package-button"
import { ProjectBridgeSelectVisibleCheckbox } from "./project-bridge-select-visible-checkbox"

export function ProjectBridgeListContent() {
    const location = useLocation()
    const refresh = useRefresh()
    const openUpload = useDialog(<ProjectBridgeUploadDialog />, { maxWidth: "sm", fullWidth: true })
    const overview = getProjectBridgeOverview.useResults(withInitialValue(IS_LOADING))
    const [search = ""] = useSearchParam("search", "")
    const [sortField, setSortField] = useReferenceState("project-bridge-sort-field", "code")
    const [sortDirection, setSortDirection] = useReferenceState("project-bridge-sort-direction", "asc")
    const scrollContainer = useScrollRestoration(
        getProjectBridgeListScrollKey({
            pathname: location.pathname,
            search: location.search,
            sortField,
            sortDirection,
        })
    )
    const loading = overview === IS_LOADING
    const overviewItems = useMemo(resolveOverviewItems, [loading, overview])
    const sorted = useMemo(resolveSortedItems, [overviewItems, search, sortDirection, sortField])
    const latestRunStatus = overview?.latestRun?.status
    const importSummary = overview?.importRecord

    useEffect(clearSelectionForImport, [importSummary?.id])

    if (loading) return <LoadingScreen />

    return (
        <PageBreadcrumbContainer title="Project Bridge" tightWidth="xl">
            <TreeToolbar.Plug deps={[importSummary?.id, latestRunStatus, overviewItems]}>
                <Stack direction="row" spacing={1}>
                    <ProjectBridgeCreateWorkPackageButton items={overviewItems} />
                    <Button variant="outlined" size="small" onClick={handleUpload}>
                        Upload JSON
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        disabled={!importSummary?.id || latestRunStatus === "RUNNING"}
                        onClick={handleGenerate}
                    >
                        Generate Rewrites
                    </Button>
                </Stack>
            </TreeToolbar.Plug>

            <Stack spacing={2} sx={{ px: 2, pt: 2, pb: 1 }}>
                <If truthy={importSummary}>
                    <ProjectBridgeImportSummaryCards importRecord={importSummary} latestRunStatus={latestRunStatus} />
                    <Else>
                        <Alert severity="warning">
                            No Project Bridge import is available yet. Upload a JSON report to create the first import.
                        </Alert>
                    </Else>
                </If>
                <SearchBoxUsingSearchParam placeholder="Search by code or schedule title…" m={0} />
            </Stack>

            <Box ref={scrollContainer} flex={1} sx={{ overflowY: "auto" }}>
                <If truthy={sorted.length}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <ProjectBridgeSelectVisibleCheckbox items={sorted} />
                                <SortableTableCell
                                    field="code"
                                    label="Code"
                                    sortField={sortField}
                                    sortDirection={sortDirection}
                                    onSort={handleSort}
                                />
                                <SortableTableCell
                                    field="title"
                                    label="Schedule"
                                    sortField={sortField}
                                    sortDirection={sortDirection}
                                    onSort={handleSort}
                                />
                                <TableCell>Mapping</TableCell>
                                <TableCell>Status</TableCell>
                                <SortableTableCell
                                    field="overallMatchPercent"
                                    label="Match"
                                    sortField={sortField}
                                    sortDirection={sortDirection}
                                    onSort={handleSort}
                                />
                                <SortableTableCell
                                    field="readySectionCount"
                                    label="Ready"
                                    sortField={sortField}
                                    sortDirection={sortDirection}
                                    onSort={handleSort}
                                />
                                <SortableTableCell
                                    field="unresolvedSectionCount"
                                    label="Unresolved"
                                    sortField={sortField}
                                    sortDirection={sortDirection}
                                    onSort={handleSort}
                                />
                                <TableCell>Types</TableCell>
                                <TableCell>Criticality</TableCell>
                                <SortableTableCell
                                    field="changeCount"
                                    label="Changes"
                                    sortField={sortField}
                                    sortDirection={sortDirection}
                                    onSort={handleSort}
                                />
                                <TableCell>Decisions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <Repeat list={sorted} item={<ProjectBridgeListRow />} keyFn={keyByItem} />
                        </TableBody>
                    </Table>
                    <Else>
                        <NoResults message="No schedules match the current Project Bridge filters." />
                    </Else>
                </If>
            </Box>
        </PageBreadcrumbContainer>
    )

    function handleSort(field) {
        if (sortField === field) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc")
            return
        }

        setSortField(field)
        setSortDirection(field === "overallMatchPercent" || field === "changeCount" ? "desc" : "asc")
    }

    async function handleGenerate() {
        try {
            await startProjectBridgeGeneration({ importId: importSummary?.id })
            refresh()
            successSnackbar("Project Bridge rewrite generation queued.")
        } catch (error) {
            errorSnackbar(`Could not start Project Bridge generation: ${error?.message || error}`)
        }
    }

    async function handleUpload() {
        try {
            const data = await openUpload()
            if (!data) return

            await uploadProjectBridgeReport({
                fileName: data.fileName,
                fileData: data.fileData,
            })
            refresh()
            successSnackbar("Project Bridge JSON uploaded and imported.")
        } catch (error) {
            errorSnackbar(`Could not upload Project Bridge report: ${error?.message || error}`)
        }
    }

    function resolveOverviewItems() {
        if (loading) return []
        return Array.isArray(overview?.items) ? overview.items : []
    }

    function resolveSortedItems() {
        const filtered = applySearch(overviewItems, `${search || ""}`.toLowerCase())
        return sortItems(filtered, sortField, sortDirection)
    }

    function clearSelectionForImport() {
        clearProjectBridgeScheduleSelection()
    }
}

function applySearch(items, search) {
    if (!search) return items

    return items.filter(function matchesItem(item) {
        return (
            `${item?.code || ""}`.toLowerCase().includes(search) ||
            `${item?.title || ""}`.toLowerCase().includes(search)
        )
    })
}

function sortItems(items, sortField, sortDirection) {
    const direction = sortDirection === "asc" ? 1 : -1

    return [...items].sort(function compareItems(left, right) {
        const leftValue = left?.[sortField]
        const rightValue = right?.[sortField]

        if (typeof leftValue === "number" || typeof rightValue === "number") {
            return (Number(leftValue || 0) - Number(rightValue || 0)) * direction
        }

        return `${leftValue || ""}`.localeCompare(`${rightValue || ""}`) * direction
    })
}

function keyByItem(item) {
    return item?.id
}
